import { Component, EventEmitter, Input, Output, inject, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogContent } from '@angular/material/dialog';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
  AbstractControl,
  FormBuilder,
} from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AlertService } from '../../../_services/alert.service';
import { LandscaperService } from '../../../_services/landscaper.service';

// Coordinates: a number inside the valid range.
function coordinate(limit: number) {
  return (control: AbstractControl) => {
    if (control.value === null || control.value === '') return null; // "required" reports that
    const value = Number(control.value);
    if (Number.isNaN(value)) return { number: true };
    return Math.abs(value) > limit ? { range: true } : null;
  };
}

@Component({
    selector: 'app-create-edit-landscaper',
    imports: [MatDialogContent, NgClass, ReactiveFormsModule, MatIconModule, MatTooltipModule],
    templateUrl: './create-edit-landscaper.component.html',
    styleUrl: './create-edit-landscaper.component.css'
})
export class CreateEditLandscaperComponent implements OnInit {

  // Works as a dialog or inside the landscaper edit page: there it gets the
  // landscaper through `pageData` and reports back through `done`.
  readonly dialogRef = inject(MatDialogRef<CreateEditLandscaperComponent>, { optional: true });
  private readonly dialogData = inject<any>(MAT_DIALOG_DATA, { optional: true });
  private readonly sanitizer = inject(DomSanitizer);
  @Input() pageData: { type: 'ADD' | 'EDIT'; landscaper?: any } | null = null;
  @Output() done = new EventEmitter<{ status: boolean }>();

  get data(): any {
    return this.pageData ?? this.dialogData ?? { type: 'ADD' };
  }

  get pageMode(): boolean {
    return !this.dialogRef;
  }

  get hasChanges(): boolean {
    return this.createEditLandscaperForm.dirty;
  }

  finish(result: { status: boolean }): void {
    if (this.dialogRef) this.dialogRef.close(result);
    else this.done.emit(result);
  }

  // A small embedded map of the coordinates typed in (kept until they change,
  // so the map doesn't reload on every keystroke elsewhere).
  private mapKey = '';
  private mapUrl: SafeResourceUrl | null = null;
  get mapEmbed(): SafeResourceUrl | null {
    const v = this.createEditLandscaperForm.value ?? {};
    const lat = Number(v.latitude);
    const lng = Number(v.longitude);
    const ok = v.latitude !== '' && v.longitude !== '' && !Number.isNaN(lat) && !Number.isNaN(lng) && lat !== 0 && lng !== 0 && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;
    const key = ok ? `${lat},${lng}` : '';
    if (key !== this.mapKey) {
      this.mapKey = key;
      this.mapUrl = key ? this.sanitizer.bypassSecurityTrustResourceUrl(`https://maps.google.com/maps?q=${encodeURIComponent(key)}&z=14&output=embed`) : null;
    }
    return this.mapUrl;
  }
  saving = false;

  createEditLandscaperForm: FormGroup = new FormGroup({});

  constructor(
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private landscrapperService: LandscaperService
  ) {}

  get isEdit(): boolean {
    return this.data.type === 'EDIT';
  }

  ngOnInit(): void {
    this.createEditLandscaperForm = this.formBuilder.group({
      storeName: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(250)]),
      contactPerson: new FormControl('', [Validators.maxLength(250)]),
      // UK and international numbers: digits with spaces, +, brackets or dashes.
      phoneNo: new FormControl('', [Validators.pattern('^\\+?[0-9 ()-]{7,20}$')]),
      email: new FormControl('', [Validators.email]),
      storeUrl: new FormControl('', [Validators.pattern('^(https?:\\/\\/)?[^\\s\\/]+\\.[a-zA-Z]{2,}(\\/\\S*)?$')]),
      fax: new FormControl(''),
      address1: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(200)]),
      address2: new FormControl('', [Validators.maxLength(200)]),
      city: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]),
      state: new FormControl('', [Validators.maxLength(100)]),
      country: new FormControl('', [Validators.maxLength(100)]),
      postCode: new FormControl('', [Validators.required, Validators.minLength(2), Validators.maxLength(20)]),
      latitude: new FormControl('', [Validators.required, coordinate(90)]),
      longitude: new FormControl('', [Validators.required, coordinate(180)]),
      addInfo: new FormControl('', [Validators.maxLength(1000)]),
      isApproved: new FormControl(true),
    });
    if (this.isEdit && this.data.landscaper) {
      const l = this.data.landscaper;
      this.createEditLandscaperForm.patchValue({
        storeName: (l.storeName ?? '').trim(),
        contactPerson: l.contactPerson ?? '',
        phoneNo: l.phoneNo ?? '',
        email: l.email ?? '',
        storeUrl: l.storeUrl ?? '',
        fax: l.fax ?? '',
        address1: l.address1 ?? '',
        address2: l.address2 ?? '',
        city: (l.city ?? '').trim(),
        state: (l.state ?? '').trim(),
        country: (l.country ?? '').trim(),
        postCode: l.postCode ?? '',
        latitude: l.latitude ?? '',
        longitude: l.longitude ?? '',
        addInfo: l.addInfo ?? '',
        isApproved: !!l.isApproved,
      });
    }
  }

  get f(): { [key: string]: AbstractControl } {
    return this.createEditLandscaperForm.controls;
  }

  // Opens a map search for the address so the coordinates can be read off it.
  get mapSearchUrl(): string {
    const v = this.createEditLandscaperForm.value;
    const lat = Number(v.latitude);
    const lng = Number(v.longitude);
    const query = v.latitude && v.longitude && lat && lng ? `${lat},${lng}` : [v.address1, v.city, v.postCode].filter(Boolean).join(', ');
    return `https://www.google.com/maps?q=${encodeURIComponent(query || 'United Kingdom')}`;
  }

  onSubmit() {
    if (this.createEditLandscaperForm.invalid) {
      this.createEditLandscaperForm.markAllAsTouched();
      this.alertService.alert('Please provide valid information', 'error');
      return;
    }
    if (this.saving) return;
    this.createEditLandscraper();
  }

  private saved() {
    this.alertService.alert(this.isEdit ? 'Landscaper updated successfully!' : 'Landscaper created successfully!', 'success');
    this.saving = false;
    this.createEditLandscaperForm.markAsPristine();
    this.finish({ status: true });
  }

  private createEditLandscraper() {
    const v = this.createEditLandscaperForm.value;
    const text = (value: string | null) => (value ?? '').trim();
    const inputParams: any = {
      storeName: text(v.storeName),
      address1: text(v.address1),
      address2: text(v.address2),
      city: text(v.city),
      state: text(v.state),
      country: text(v.country) || null,
      postCode: text(v.postCode),
      latitude: String(v.latitude).trim(),
      longitude: String(v.longitude).trim(),
      phoneNo: text(v.phoneNo) || null,
      fax: text(v.fax) || null,
      storeUrl: text(v.storeUrl) || null,
      email: text(v.email) || null,
      contactPerson: text(v.contactPerson) || null,
      addInfo: text(v.addInfo) || null,
      isApproved: !!v.isApproved,
    };
    if (this.isEdit && this.data.landscaper) inputParams.storeId = this.data.landscaper.storeId;

    this.saving = true;
    this.landscrapperService
      .createEditLandscraper(inputParams, this.data.type)
      .subscribe({
        next: () => this.saved(),
        // Stay open on a real failure so nothing typed is lost.
        error: (error) => {
          if (LandscaperService.succeeded(error)) return this.saved();
          this.saving = false;
          this.alertService.alert(error.error?.message || 'Could not save the landscaper', 'error');
        },
      });
  }
}
